import { Flex, Text, Box, IconButton } from "@chakra-ui/react";
import { RModal } from "components";
import { IconCar, IconDoubleArrow, IconFlame, IconForecastGg, IconInfoSilver, IconRuler } from "icons";
import { conditionsConfig } from "components/Conditions/conditions.config";
import { useState, useCallback } from "react";
import ImpactTag from "components/ImpactTag/ImpactTag";

// Define the impacts for each condition type
const conditionImpacts = {
  trackTemperature: ["Tires"],
  airTemperature: ["Speed", "Cooling"],
  wind: ["Fast Turns"],
  rain: ["Tires", "Stability"],
  roughness: ["Tires"],
  tarmacCondition: ["Slow Turns"],
};

// Define descriptions for each condition type
const conditionDescriptions = {
  airTemperature: "The surrounding atmospheric temperature.",
  trackTemperature: "The temperature of the racing surface.",
  wind: "The strength of air movement across the track.",
  rain: "The amount of precipitation on the track.",
  roughness: "A measure of track bump heights.",
  tarmacCondition: "A measure of track surface condition.",
};

// Define a type for carousel slides
interface SlideData {
  type: keyof typeof conditionsConfig;
  icon: React.ReactNode;
  title: string;
  description: string;
  impacts: string[];
  variants: Record<string, {
    text: string;
    textColor: string;
    backgroundColor: string;
  }>;
}

interface ForecastModalProps {
  isOpen: boolean;
  onClose: () => void;
  slides?: SlideData[];
}

// Create slides from the condition config
const createDefaultSlides = (): SlideData[] => {
  return Object.keys(conditionsConfig).map((key) => {
    const conditionKey = key as keyof typeof conditionsConfig;
    const config = conditionsConfig[conditionKey];
    const Icon = config.icon;
    
    return {
      type: conditionKey,
      icon: <Icon />,
      title: config.label.toUpperCase(),
      description: conditionDescriptions[conditionKey],
      impacts: conditionImpacts[conditionKey],
      variants: config.variants,
    };
  });
};

const ForecastModal = ({ 
  isOpen, 
  onClose,
}: ForecastModalProps) => {
  const [slides] = useState<SlideData[]>(createDefaultSlides());
  const [currentSlide, setCurrentSlide] = useState(0);

  const handlePrevious = useCallback(() => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  }, [slides.length]);

  const handleNext = useCallback(() => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  }, [slides.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  const currentCondition = slides[currentSlide];

  return (
    <RModal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      title="FORECAST"
      icon={<IconForecastGg />}
    >
      <Flex
        flexDir="column"
        gap="4"
      >
        <Text
          fontSize={{ base: '0.75rem', md: '0.875rem'}}
          lineHeight={{ base: '0.875rem', md: '1.125rem'}}
          fontWeight={500}
          color="white.80"
        >
          Forecasted conditions are broken into six types, each with six ranges that influence car performance differently.
        </Text>

        {/* Carousel slide */}
        <Flex
          position="relative"
          w="full"
          borderRadius="0.5rem"
          background="linear-gradient(90deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 100%)"
          p="1rem"
          _before={{
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, rgba(79, 197, 186, 0.6) 0%, rgba(79, 197, 186, 0.06) 100%)',
            borderRadius: '0.5rem',
            padding: '2px',
            mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            maskComposite: "exclude",
            zIndex: -1,
          }}
        >
          <Flex
            flexDir="column"
            gap="0.5"
            w="full"
          >
            <Flex
              alignItems="center"
              gap="0.5rem"
              pl="0.25rem"
              mb="1"
            >
              {currentCondition.icon}

              <Text
                fontSize={{ base: '0.875rem', md: '1.125rem'}}
                lineHeight={{ base: '1.125rem', md: '1.5rem'}}
                fontWeight={400}
                color="white.80"
                fontFamily="heading"
              >
                {currentCondition.title} 
                {(currentCondition.title === "TRACK" || currentCondition.title === "AIR") && " (ºC)" }
                {(currentCondition.title === "WIND") && " (KpH)" }
              </Text>
            </Flex>

            <Flex
              background="linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)"
              py="0.5rem"
              px="0.75rem"
              w="full"
              borderRadius="0.25rem"
              gap="2"
              alignItems="center"
            >
              <IconInfoSilver />

              <Text
                fontSize={{ base: '0.875rem', md: '1.125rem'}}
                lineHeight={{ base: '1.125rem', md: '1.5rem'}}
                fontWeight={400}
                color="white.80"
              >
                {currentCondition.description}
              </Text>
            </Flex>

            <Flex
              background="linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)"
              py="0.5rem"
              px="0.75rem"
              w="full"
              borderRadius="0.25rem"
              flexDir="row"
              justifyContent="space-between"
              gap="2"
            >
              <Flex alignItems="center" gap="2">
                <IconCar 
                  width="16px"
                  height="16px"
                />

                <Text
                  fontSize={{ base: '0.875rem', md: '1.125rem'}}
                  lineHeight={{ base: '1.125rem', md: '1.5rem'}}
                  fontWeight={500}
                  color="white.80"
                >
                  Impacts
                </Text>
              </Flex>

              <Flex pl="0.25rem" flexWrap="wrap" gap="2" justifyContent="flex-end">
                {currentCondition.impacts.map((impact, index) => (
                  <ImpactTag
                    key={`impact-${index}`}
                    title={impact}
                  />
                ))}
              </Flex>
            </Flex>

            <Flex
              background="linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)"
              py="0.5rem"
              px="0.75rem"
              w="full"
              borderRadius="0.25rem"
              flexDir="row"
              justifyContent="space-between"
              gap="2"
            >
              <Flex alignItems="center" gap="2">
                <IconRuler />

                <Text
                  fontSize={{ base: '0.875rem', md: '1.125rem'}}
                  lineHeight={{ base: '1.125rem', md: '1.5rem'}}
                  fontWeight={500}
                  color="white.80"
                >
                  Range
                </Text>
              </Flex>

              <Flex 
                pl="0.25rem" 
                gap="2" 
                flexWrap="wrap"
                justifyContent="flex-end"
              >
                {Object.values(currentCondition.variants).map((variant, index) => (
                  <Flex 
                    key={`range-${index}`}
                    backgroundColor={variant.backgroundColor}
                    color={variant.textColor}
                    fontSize={{ base: '0.75rem', md: '0.875rem'}}
                    fontWeight="600"
                    px="2"
                    py="1"
                    borderRadius="0.25rem"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {variant.text}
                  </Flex>
                ))}
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        {/* Carousel pagination and navigation */}
        <Flex justify="center" align="center" mt="2" gap="2">
          {/* Previous button */}
          <IconButton
            aria-label="Previous slide"
            icon={<IconDoubleArrow transform="rotate(180deg)" />}
            onClick={handlePrevious}
            variant="unstyled"
            display="flex"
            isDisabled={currentSlide === 0}
            alignItems="center"
            justifyContent="center"
            background="linear-gradient(225deg, rgba(240, 240, 240, 0.1) 0%, rgba(197, 197, 197, 0.1) 18%, rgba(176, 176, 176, 0.1) 73%, rgba(126, 126, 126, 0.1) 100%)"
            p="3"
            position="relative"
            _hover={{
              background: 'rgba(240, 240, 240, 0.2)',
            }}
            transition="background 0.3s ease"
            _before={{
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(360deg, rgba(240, 240, 240, 0.3) 0%, rgba(240, 240, 240, 0.12) 100%)',
              padding: '2px',
              borderRadius: '0.25rem',
              mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              maskComposite: "exclude",
            }}
            minW="auto"
            h="auto"
          />

          {/* Pagination dots */}
          <Flex gap="2">
            {slides.map((_, index) => (
              <Box
                key={`dot-${index}`}
                w="0.75rem"
                h="0.75rem"
                borderRadius="full"
                bg={currentSlide === index ? "white" : "rgba(255, 255, 255, 0.3)"}
                cursor="pointer"
                onClick={() => goToSlide(index)}
              />
            ))}
          </Flex>

          {/* Next button */}
          <IconButton
            aria-label="Next slide"
            icon={<IconDoubleArrow />}
            onClick={handleNext}
            variant="unstyled"
            display="flex"
            isDisabled={currentSlide === slides.length - 1}
            alignItems="center"
            justifyContent="center"
            background="linear-gradient(225deg, rgba(240, 240, 240, 0.1) 0%, rgba(197, 197, 197, 0.1) 18%, rgba(176, 176, 176, 0.1) 73%, rgba(126, 126, 126, 0.1) 100%)"
            p="3"
            position="relative"
            minW="auto"
            h="auto"
            _hover={{
              background: 'rgba(240, 240, 240, 0.2)',
            }}
            transition="background 0.3s ease"
            _before={{
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(360deg, rgba(240, 240, 240, 0.3) 0%, rgba(240, 240, 240, 0.12) 100%)',
              padding: '2px',
              borderRadius: '0.25rem',
              mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              maskComposite: "exclude",
            }}
          />
        </Flex>
      </Flex>
    </RModal>
  );
};

export default ForecastModal;